// TODO: Assessment remote cleanup, delete this file. See "Cleanup" in docs/RemoteModules.md
import { chain } from 'lodash';
import {
  type ExamV3ExportTemplate,
  pageOrientations,
  sortDirections,
  templateTypes,
} from 'types/examV3ExportTemplates.type';
import { Column } from 'types/reservationSettingsColumnValues.type';
import { z } from 'zod';

const superRefineColumns =
  (message: string) => (val: Column[], ctx: z.RefinementCtx) => {
    const totalWidth = chain(val)
      .map((v) => v.width ?? 0)
      .sum()
      .value();
    if (totalWidth > 100) {
      ctx.issues.push({
        path: [0, 'width'],
        code: 'too_big',
        maximum: 100,
        message,
        inclusive: true,
        input: val,
        type: 'array',
        origin: 'array',
      });
    }
  };

const mappedValueSchema = z.object({
  label: z
    .string({ message: 'Label is required' })
    .max(100, { message: 'Label can be at most 100 characters long' }),
  type: z.string({ message: 'Invalid type' }),
  value: z
    .string({ message: 'Invalid value' })
    .min(1, { message: 'Invalid value' }),
});

const mappedColumnSchema = mappedValueSchema.extend({
  width: z.number().gte(1).lte(100).optional().nullable(),
});

const templateSchema = z.object({
  name: z
    .string({ message: 'Name is required' })
    .max(100, { message: 'Name can be at most 100 characters long' })
    .min(1, { message: 'Name is required' }),
  title: z
    .string({ message: 'Title is required' })
    .max(200, { message: 'Title can be at most 200 characters long' })
    .min(1, { message: 'Title is required' }),
  description: z.string().optional(),
  templateType: z.enum(templateTypes),
  timezone: z.string().optional(),
  pageOrientation: z.enum(pageOrientations, {
    message: 'Page orientation has an invalid value',
  }),
  mainHeader: z.array(mappedValueSchema),
  mainHeaderNoOfColumns: z.number().min(1).max(10),
  seatingList: z.object({
    studentList: z.object({
      sort: z
        .enum(sortDirections, {
          message: 'Student list columns has an invalid sort field',
        })
        .default('asc')
        .optional(),
      sortBy: z.number().int().min(0).default(0).optional(),
      title: z.object({
        type: z.string({ message: 'Student seating list title is required' }),
        value: z
          .string({ message: 'Student seating list value is required' })
          .min(1, { message: 'Student seating list value is required' }),
      }),
      header: z.array(mappedValueSchema),
      headerNoOfColumns: z.number().min(1).max(10),
      columns: z
        .array(mappedColumnSchema)
        .superRefine(
          superRefineColumns(
            'Total widths of Student list columns cannot exceed 100%'
          )
        ),
    }),
    examRange: z.object({
      sort: z
        .enum(sortDirections, {
          message: 'Exam range has an invalid sort field',
        })
        .default('asc')
        .optional(),
      sortBy: z.number().int().min(0).default(0).optional(),
      columns: z
        .array(mappedColumnSchema)
        .superRefine(
          superRefineColumns(
            'Total widths of Exam range columns cannot exceed 100%'
          )
        ),
    }),
  }),
});

export const validateTemplate = (template: Partial<ExamV3ExportTemplate>) => {
  return templateSchema.safeParse(template);
};

export const validateTemplateAsync = async (
  template: Partial<ExamV3ExportTemplate>
) => {
  return templateSchema.safeParseAsync(template);
};
